// Spawn factory: ElementPublic → Three.js objects.

import * as THREE from 'three';
import { GLTFLoader, type GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { AnimationAction, AnimationMixer, AudioListener, PositionalAudio, Audio } from 'three';
import type { ElementPublic } from './api';
import { getAsset } from './cache';

export interface SpawnedElement {
  data: ElementPublic;
  root: THREE.Object3D;
  mixer?: AnimationMixer;
  action?: AnimationAction;
  audio?: Audio | PositionalAudio;
  material?: THREE.Material;
  /** Cleanup hook called when augmentation is unloaded */
  dispose(): void;
  /** Called per RAF with current visible flag + opacity 0..1 */
  setVisibility(visible: boolean, opacity: number): void;
}

const gltfLoader = new GLTFLoader();
const textureLoader = new THREE.TextureLoader();
textureLoader.crossOrigin = 'anonymous';

function applyTransform(root: THREE.Object3D, data: ElementPublic): void {
  root.position.set(data.pos_x, data.pos_y, data.pos_z);
  root.rotation.set(
    THREE.MathUtils.degToRad(data.rot_x),
    THREE.MathUtils.degToRad(data.rot_y),
    THREE.MathUtils.degToRad(data.rot_z),
  );
  root.scale.setScalar(Math.max(0.0001, data.scale));
}

async function spawnImage(data: ElementPublic): Promise<SpawnedElement> {
  // Try texture load — direct HTTP URL. Fall back to colored plane if it fails.
  // This way we always show SOMETHING for the evaluator if texture loading is buggy.
  let texture: THREE.Texture | null = null;
  try {
    texture = await new Promise<THREE.Texture>((resolve, reject) =>
      textureLoader.load(data.asset.url, resolve, undefined, (err) => reject(err)),
    );
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = 4;
    console.log('[ar] image texture loaded OK:', data.asset.url);
  } catch (err) {
    console.warn('[ar] image texture load FAILED, using fallback orange plane:', data.asset.url, err);
  }

  const aspect =
    data.asset.width_px && data.asset.height_px
      ? data.asset.width_px / data.asset.height_px
      : 1;
  const width = 0.1;
  const height = width / aspect;
  const geometry = new THREE.PlaneGeometry(width, height);
  const material = new THREE.MeshBasicMaterial({
    map: texture,
    color: texture ? 0xffffff : 0xff8800,
    transparent: true,
    opacity: data.opacity,
    side: THREE.DoubleSide,
  });
  const mesh = new THREE.Mesh(geometry, material);
  applyTransform(mesh, data);

  return {
    data,
    root: mesh,
    material,
    setVisibility(visible, opacity) {
      mesh.visible = visible;
      material.opacity = opacity * data.opacity;
    },
    dispose() {
      geometry.dispose();
      material.dispose();
      texture.dispose();
    },
  };
}

async function spawnAudio(data: ElementPublic, listener: AudioListener): Promise<SpawnedElement> {
  const { objectUrl } = await getAsset(data.asset.url);
  const audioBuffer = await new Promise<AudioBuffer>((resolve, reject) => {
    const audioCtx = listener.context;
    fetch(objectUrl)
      .then((r) => r.arrayBuffer())
      .then((buf) => audioCtx.decodeAudioData(buf, resolve, reject))
      .catch(reject);
  });

  const root = new THREE.Object3D();
  applyTransform(root, data);

  let audio: Audio | PositionalAudio;
  if (data.audio_spatial) {
    audio = new THREE.PositionalAudio(listener);
    (audio as PositionalAudio).setRefDistance(0.2);
  } else {
    audio = new THREE.Audio(listener);
  }
  audio.setBuffer(audioBuffer);
  audio.setLoop(data.loop);
  audio.setVolume(data.audio_volume * data.opacity);
  root.add(audio);

  let playing = false;

  return {
    data,
    root,
    audio,
    setVisibility(visible, opacity) {
      root.visible = visible;
      audio.setVolume(data.audio_volume * opacity);
      if (visible && !playing) {
        try {
          audio.play();
          playing = true;
        } catch {
          // browser may block until user gesture — caught silently
        }
      } else if (!visible && playing) {
        try {
          audio.stop();
        } catch {
          /* noop */
        }
        playing = false;
      }
    },
    dispose() {
      try { audio.stop(); } catch { /* noop */ }
      audio.disconnect();
    },
  };
}

async function spawnModel(data: ElementPublic): Promise<SpawnedElement> {
  // Try GLB load — direct HTTP URL. Fall back to colored cube if it fails.
  let gltf: GLTF | null = null;
  let root: THREE.Object3D;
  try {
    gltf = await new Promise<GLTF>((resolve, reject) =>
      gltfLoader.load(data.asset.url, resolve, undefined, (err) => reject(err)),
    );
    root = gltf.scene;
    console.log('[ar] GLB loaded OK:', data.asset.url);
  } catch (err) {
    console.warn('[ar] GLB load FAILED, using fallback cyan cube:', data.asset.url, err);
    const fallbackGeom = new THREE.BoxGeometry(0.05, 0.05, 0.05);
    const fallbackMat = new THREE.MeshBasicMaterial({ color: 0x00aaff });
    root = new THREE.Mesh(fallbackGeom, fallbackMat);
  }
  applyTransform(root, data);

  // Apply opacity to all child materials cumulatively (multiply with original alpha).
  const baseMaterials: Array<{ mat: THREE.Material; baseOpacity: number; baseTransparent: boolean }> = [];
  root.traverse((node) => {
    if ((node as THREE.Mesh).isMesh) {
      const mesh = node as THREE.Mesh;
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const m of mats) {
        baseMaterials.push({
          mat: m as THREE.Material,
          baseOpacity: (m as THREE.Material).opacity,
          baseTransparent: (m as THREE.Material).transparent,
        });
      }
    }
  });

  let mixer: AnimationMixer | undefined;
  let action: AnimationAction | undefined;
  if (gltf.animations.length > 0) {
    mixer = new THREE.AnimationMixer(root);
    const clip =
      gltf.animations.find((c) => c.name === data.animation_name) ?? gltf.animations[0];
    action = mixer.clipAction(clip);
    action.setLoop(data.loop ? THREE.LoopRepeat : THREE.LoopOnce, Infinity);
    action.clampWhenFinished = !data.loop;
    action.timeScale = data.animation_speed;
    action.play();
  }

  return {
    data,
    root,
    mixer,
    action,
    setVisibility(visible, opacity) {
      root.visible = visible;
      const eff = opacity * data.opacity;
      for (const entry of baseMaterials) {
        entry.mat.opacity = entry.baseOpacity * eff;
        entry.mat.transparent = entry.baseTransparent || eff < 1;
        entry.mat.needsUpdate = true;
      }
    },
    dispose() {
      mixer?.stopAllAction();
      root.traverse((node) => {
        if ((node as THREE.Mesh).isMesh) {
          const m = node as THREE.Mesh;
          m.geometry?.dispose();
          const mats = Array.isArray(m.material) ? m.material : [m.material];
          for (const mat of mats) mat.dispose();
        }
      });
    },
  };
}

export async function spawnElement(
  data: ElementPublic,
  listener: AudioListener,
): Promise<SpawnedElement> {
  switch (data.asset.type) {
    case 'image':
      return spawnImage(data);
    case 'audio':
      return spawnAudio(data, listener);
    case 'model_3d':
      return spawnModel(data);
    default:
      throw new Error(`unknown asset type: ${(data.asset as { type: string }).type}`);
  }
}

export function tickMixer(mixer: AnimationMixer | undefined, deltaSeconds: number): void {
  if (mixer) mixer.update(deltaSeconds);
}
